<template>
  <div class="loading-overlay bg-light">
    <template v-if="failed">
      <p role="alert" class="sso-landing__failure">
        {{
          $gettext(
            "We couldn't match your account on this version of the platform. Please contact our team for help."
          )
        }}
      </p>
      <router-link class="sso-landing__login-link" :to="{ name: 'login' }">{{
        $gettext("Login")
      }}</router-link>
    </template>
    <template v-else>
      <div class="my-4">
        <BSpinner class="mr-3" />
      </div>
      {{
        $gettext(
          "Please wait while we redirect you to your employer dashboard."
        )
      }}
    </template>
  </div>
</template>
<script>
import { BSpinner } from "bootstrap-vue";
import { mapActions, mapGetters } from "vuex";
import { updateUserIntercom } from "@/utils/intercom";
import store from "@/store";

// Captured once at module load: `window.location` is itself the assignment target below, so
// reading `.origin` lazily would race the navigation instead of the page's real origin.
const PAGE_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

// Resolve `redirect` through the real URL parser instead of a raw string check (Q2): only a
// value that both starts with a single "/" and resolves to this origin is followed.
const toSafeRedirectPath = value => {
  if (typeof value !== "string" || !/^\/(?!\/)/.test(value)) return null;
  try {
    const url = new URL(value, PAGE_ORIGIN);
    // Dot segments can normalise to a "//host" pathname, which is protocol-relative again.
    if (url.origin !== PAGE_ORIGIN || url.pathname.startsWith("//")) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

export default {
  components: { BSpinner },
  props: {
    selector: {
      type: String,
      required: true
    },
    token: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      failed: false
    };
  },
  computed: {
    ...mapGetters("me", ["user"])
  },
  async beforeRouteEnter(to, from, next) {
    if (to.query?.lg) {
      await store.dispatch("me/setLg", to.query.lg);
    }
    next();
  },
  mounted() {
    this.loginSso({ selector: this.selector, token: this.token })
      .then(() => {
        updateUserIntercom(this.user);
        const redirect = this.$route.query.redirect;
        // Same-origin PATH only (ES1661-03, hardened Q2): a raw string check misses browser URL
        // quirks (backslash-as-slash, stripped control chars) that turn "/\evil.example" or a
        // tab-laced "/\t/evil.example" into a protocol-relative redirect once resolved.
        // The pair is consumed, but the next page's trackers still send document.referrer.
        window.history.replaceState(null, "", "/sso");
        window.location = toSafeRedirectPath(redirect) ?? "/dashboard";
      })
      .catch(() => {
        // A refused pair explains itself instead of silently landing on login (ES-1661 D10).
        this.failed = true;
      });
  },
  methods: {
    ...mapActions("me", ["loginSso"])
  }
};
</script>
<style scoped>
.sso-landing__failure {
  max-width: 44ch;
  padding: 0 16px;
  text-align: center;
}

/* DR-01: this failure-screen link only, not a global link restyle (D11) */
.sso-landing__login-link {
  color: #6c38d2;
  text-decoration: underline;
}

.sso-landing__login-link:focus-visible {
  outline: 2px solid #6c38d2;
  outline-offset: 2px;
}
</style>
